import { usePollBrandVisualAnalysis } from 'hooks/queries/brandVisualAnalyses/usePollBrandVisualAnalysis'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import Styles from './BrandWizardVisualAnalysisModal.module.scss'
import { Section } from './Section'
import { Color } from './Color'
import { Logo } from './Logo'
import { Font } from './Font'
import { useBrandWizardVisualAnalysisContext } from './BrandWizardVisualAnalysisContext'
import { MutableRefObject, useEffect, useState } from 'react'
import { Portal } from 'components/Portal/Portal'
import { GenerateAgainButton } from './GenerateAgainButton'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { ShimmeringColor, ShimmeringFont } from './LoadingState'
import { ConfirmButton } from './ConfirmButton'
import { getPropsFromIntegration } from 'components/IntegrationsModal/ManageIntegrationAccounts/utils'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Carousel } from 'components/Carousel/Carousel'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { BrandVisualKit } from 'types'

interface Props {
  initialId: number
  continueContainerRef: MutableRefObject<HTMLDivElement | null>
  regenerateContainerRef: MutableRefObject<HTMLDivElement | null>
  integration: ExternalIntegration
  onConfirm: (kit: BrandVisualKit) => void
}

export const Body = ({ initialId, integration, continueContainerRef, regenerateContainerRef, onConfirm }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const [id, setId] = useState(initialId)

  const { result, onChange } = useBrandWizardVisualAnalysisContext()
  const { data, isLoading } = usePollBrandVisualAnalysis({ workspaceId, id })
  const { name } = getPropsFromIntegration(integration)
  const { isMobile } = useResponsiveDesign()
  const isLoadingPendingOrStarted =
    isLoading || data?.brand_visual_analysis.status === 'pending' || data?.brand_visual_analysis.status === 'started'

  useEffect(() => {
    if (data?.brand_visual_analysis.result) {
      onChange(data.brand_visual_analysis.result)
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    return null
  }

  const { logo, font_pairings, colors: brandColors } = result
  const pairing = font_pairings[0]

  const colors = brandColors.map((color, index) => (
    <Color
      key={index}
      color={color}
      onChange={(color) => {
        onChange({ ...result, colors: result.colors.map((c, i) => (i === index ? color : c)) })
      }}
    />
  ))

  return (
    <div className={Styles.body}>
      <div className={Styles.row}>
        <div className={Styles.logoContainer}>
          <Section title="Logo">
            {isLoadingPendingOrStarted && <ShimmeringColor aspectRatio={1} width={isMobile ? 110 : 135} />}
            {!isLoadingPendingOrStarted && <Logo logo={logo} />}
          </Section>
        </div>

        <Section title="Your Palette">
          <ResponsiveDesign.Mobile>
            <div className={Styles.colorsCarousel}>
              <Carousel>
                {isLoadingPendingOrStarted &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <ShimmeringColor aspectRatio={92 / 110} width={92} key={i} />
                  ))}
                {!isLoadingPendingOrStarted && colors}
              </Carousel>
            </div>
          </ResponsiveDesign.Mobile>
          <ResponsiveDesign.Desktop>
            <div className={Styles.colors}>
              {isLoadingPendingOrStarted &&
                Array.from({ length: 5 }).map((_, i) => <ShimmeringColor aspectRatio={1} width={135} key={i} />)}
              {!isLoadingPendingOrStarted && colors}
            </div>
          </ResponsiveDesign.Desktop>
        </Section>
      </div>

      {isLoadingPendingOrStarted && (
        <div className={Styles.row}>
          <Section title="Fonts">
            <ShimmeringFont label="Large Text" width={385} height={36} />
            <ShimmeringFont label="Medium Text" width={520} height={16} />
          </Section>
        </div>
      )}

      {!isLoadingPendingOrStarted && pairing && (
        <div className={Styles.row}>
          <Section title="Fonts">
            <Font
              label="Large Text"
              font={pairing.display_font}
              size={38}
              onChange={(font) =>
                onChange({ ...result, font_pairings: [{ ...result.font_pairings[0], display_font: font }] })
              }
            >
              A Quick Brown Fox
            </Font>
            <Font
              label="Medium Text"
              font={pairing.body_font}
              size={16}
              onChange={(font) =>
                onChange({ ...result, font_pairings: [{ ...result.font_pairings[0], body_font: font }] })
              }
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et
              dolore magna aliqua.
            </Font>
          </Section>
        </div>
      )}

      <Portal container={continueContainerRef}>
        <ConfirmButton
          name={name || 'Brand Kit'}
          fullWidth={isMobile}
          isDisabled={isLoadingPendingOrStarted}
          onConfirm={onConfirm}
        />
      </Portal>
      <Portal container={regenerateContainerRef}>
        <GenerateAgainButton
          integration={integration}
          onGenerate={({ id }) => setId(id)}
          variant={isMobile ? 'tertiary' : 'secondary'}
          fullWidth={isMobile}
          frontIcon={ArrowRefresh2}
          isDisabled={isLoadingPendingOrStarted}
        />
      </Portal>
    </div>
  )
}
